import { Lock, AlertTriangle } from 'lucide-react';
import { AvgReviewTimeCard } from './AvgReviewTimeCard';
import { ReviewerLoadChart } from './ReviewerLoadChart';
import { ReviewThroughputChart } from './ReviewThroughputChart';
import { FindingTypeChart } from './FindingTypeChart';
import { useTeamMonitoring } from '@/hooks/useTeamMonitoring';
import { useAuth } from '@/hooks/useAuth';

// ─── Access denied state ─────────────────────────────────────────────────────

function AccessDenied() {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center py-20 text-center gap-3"
    >
      <Lock className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">Admin access required</p>
      <p className="text-xs text-muted-foreground max-w-xs">
        Team monitoring metrics are visible to administrators only.
      </p>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Team Monitoring tab — FR-083 + FR-084. Admin-only.
 *
 * Four panels:
 *   1. AvgReviewTimeCard — avg review time, override rate, escalation rate (FR-083)
 *   2. ReviewerLoadChart — bar chart of reviews per reviewer (FR-083)
 *   3. ReviewThroughputChart — line chart of daily reviews over 30d (FR-083)
 *   4. FindingTypeChart — top finding types frequency report (FR-084)
 *
 * Non-admins see an access denied message — the tab itself is hidden in
 * DashboardTabs for non-admins, but this guard provides defence in depth.
 */
export function TeamMonitoringTab() {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return <TeamMonitoringContent />;
}

// ─── Admin content (rendered only when isAdmin) ───────────────────────────────

function TeamMonitoringContent() {
  const { data, isLoading, error, refetch } = useTeamMonitoring();

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-foreground">Team Monitoring</h2>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 flex items-center gap-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load team monitoring data.</span>
          <button type="button" onClick={() => void refetch()} className="underline hover:no-underline ml-2">
            Retry
          </button>
        </div>
      )}

      {/* Top row: KPI card (narrow) + reviewer load chart */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <AvgReviewTimeCard
          avgMinutes={data?.avg_review_time_minutes ?? null}
          overrideRatePct={data?.override_rate_pct ?? null}
          escalationRatePct={data?.escalation_rate_pct ?? null}
          loading={isLoading}
        />
        <div className="sm:col-span-2">
          <ReviewerLoadChart
            data={data?.reviewer_load ?? []}
            loading={isLoading}
          />
        </div>
      </div>

      {/* Bottom row: throughput line chart + finding type bar chart */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ReviewThroughputChart
          data={data?.throughput_last_30d ?? []}
          loading={isLoading}
        />
        <FindingTypeChart
          data={data?.top_finding_types ?? []}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
